import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useTinkLoader } from './TinkLoader'
import { useFTSWidgetContext } from '../context'
import { useTrack } from '../context/track'
import { TrackingEvent } from '../utils/tracking'

function isHtmlNode(node: Node): node is HTMLElement {
  return (node as unknown as HTMLElement).classList !== undefined
}

const BANK_CODE_IDENTIFIER = '#XS2A-bank_code'
const SUBMIT_BUTTON_IDENTIFIER = '.xs2a-submit'

function getElement(node: Node, identifier: string): HTMLInputElement | null {
  if (!isHtmlNode(node)) {
    return null
  }

  return node.querySelector(`${identifier}`)
}

interface FormProps {
  defaultValue?: string
  submitButtonStep1Label?: string
  submitButtonStep2Label?: string
  onBankCodeChange?: (bankCode: string) => void
  isThemeLoaded: boolean
}

const Form = memo(
  ({
    submitButtonStep1Label,
    submitButtonStep2Label,
    onBankCodeChange,
    defaultValue = '',
    isThemeLoaded,
  }: FormProps) => {
    const {
      state: { wizardKey },
    } = useFTSWidgetContext()
    const formRef = useRef<HTMLDivElement | null>(null)

    const { tink } = useTinkLoader()
    const track = useTrack()

    useEffect(() => {
      if (tink && !!wizardKey) {
        tink.init()
      }
    }, [tink, wizardKey])

    const onInputChange = useCallback(
      (event: Event) => {
        if (!onBankCodeChange) {
          return
        }

        track(TrackingEvent.DACBankCodeChange)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onBankCodeChange((event.target as any).value)
      },
      [onBankCodeChange, track],
    )

    const handleSubmitButtonLable = useCallback(
      (node: Node) => {
        const submitBtn = getElement(node, SUBMIT_BUTTON_IDENTIFIER)
        if (!submitBtn) {
          return
        }
        if (submitButtonStep1Label && submitButtonStep2Label) {
          if (tink?.isLogin()) {
            submitBtn.innerHTML = submitButtonStep2Label
          } else {
            submitBtn.innerHTML = submitButtonStep1Label
          }
        }
      },
      [submitButtonStep1Label, submitButtonStep2Label, tink],
    )

    const handleAddedNode = useCallback(
      (node: Node) => {
        handleSubmitButtonLable(node)
        const bankCodeElement = getElement(node, BANK_CODE_IDENTIFIER)
        if (!bankCodeElement) {
          return
        }

        if (bankCodeElement.value.length === 0) {
          bankCodeElement.value = defaultValue
        }

        bankCodeElement.addEventListener('input', onInputChange)
        bankCodeElement.addEventListener('change', onInputChange)
      },
      [defaultValue, handleSubmitButtonLable, onInputChange],
    )

    const handleRemovedNode = useCallback(
      (node: Node) => {
        const bankCodeElement = getElement(node, BANK_CODE_IDENTIFIER)
        if (!bankCodeElement) {
          return
        }

        bankCodeElement.removeEventListener('input', onInputChange)
        bankCodeElement.removeEventListener('change', onInputChange)
      },
      [onInputChange],
    )

    const onObserve = useCallback<MutationCallback>(
      (mutationsList) => {
        for (const { addedNodes, removedNodes } of mutationsList) {
          addedNodes.forEach(handleAddedNode)
          removedNodes.forEach(handleRemovedNode)
        }
      },
      [handleAddedNode, handleRemovedNode],
    )

    useEffect(() => {
      if (!formRef.current) {
        return undefined
      }

      const formObserver = new MutationObserver(onObserve)

      formObserver.observe(formRef.current, {
        childList: true,
        subtree: true,
      })

      return () => {
        formObserver.disconnect()
      }
    }, [onObserve])

    const wrapperStyles: CSSProperties = useMemo(
      () => ({
        visibility: isThemeLoaded ? 'visible' : 'hidden',
      }),
      [isThemeLoaded],
    )
    return (
      <div style={wrapperStyles}>
        <div
          data-testid="XS2A-Form"
          id="XS2A-Form"
          data-xs2a={wizardKey}
          ref={formRef}
        />
      </div>
    )
  },
)

Form.displayName = 'Form'

export { Form }
